import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const PAYPAL_ME_USERNAME = "FeliciaMontano17";

const CATEGORIES = [
  { id: "pro",     name: "Professional Contest Entry", desc: "KCBS 4 Meat Contest", price: 350 },
  { id: "back",    name: "Backyard Contest Entry",     desc: "4 Meat Contest — Brisket, Ribs, Pork, Chicken", price: 250 },
  { id: "kids",    name: "Little Smokies Kids Que",    desc: "15 yr olds & under. Protein to be announced.", price: 25 },
  { id: "sauce",   name: "Sauce Category",             desc: "Teams may submit multiple paid entries.", price: 25 },
  { id: "peoples", name: "People's Choice (Mandatory)", desc: "A pork butt will be provided. You may pre-smoke or cook on site.", price: 0 },
];

const empty = {
  first_name: "", last_name: "", team_name: "",
  trailer_size: "",
  street: "", street2: "", city: "", state: "FL", zip_code: "",
  email: "", phone: "", notes: "",
};

export default function EventRegistrationForm() {
  const [form, setForm] = useState(empty);
  const [selected, setSelected] = useState({}); // { [id]: qty }
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggle = (id) => setSelected((s) => {
    const n = { ...s };
    if (n[id]) delete n[id]; else n[id] = 1;
    return n;
  });
  const setQty = (id, v) => setSelected((s) => ({ ...s, [id]: Math.max(1, Math.min(10, Number(v) || 1)) }));

  const chosen = useMemo(() => CATEGORIES.filter((c) => selected[c.id]), [selected]);
  const total = useMemo(
    () => chosen.reduce((sum, c) => sum + c.price * selected[c.id], 0),
    [chosen, selected]
  );
  const totalStr = total.toFixed(2);
  const paidTotal = total; // amount PayPal will charge

  const formValid = form.first_name && form.last_name && form.street && form.city && form.state && form.zip_code && form.email && form.phone && chosen.length > 0;

  const entriesPayload = () => chosen.map((c) => ({
    name: c.name,
    unit_price: c.price,
    qty: selected[c.id],
  }));

  const submitRegistration = async (extra = {}) => {
    const payload = { ...form, entries: entriesPayload(), event_name: "Small Town Showdown", ...extra };
    await api.post("/event-registration", payload);
  };

  // Free registration path (only if everything selected is $0)
  const submitFree = async (e) => {
    e.preventDefault();
    if (!formValid) {
      toast.error("Fill required fields and pick at least one entry.");
      return;
    }
    setBusy(true);
    try {
      await submitRegistration();
      toast.success("Registration submitted! We'll be in touch shortly.");
      setForm(empty);
      setSelected({});
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Couldn't submit — check required fields.");
    } finally {
      setBusy(false);
    }
  };

  // Paid path: save registration first (as pending payment) then open PayPal.me
  const submitPaid = async () => {
    if (!formValid) {
      toast.error("Fill required fields and pick at least one entry.");
      return;
    }
    setBusy(true);
    try {
      const notes = `${form.notes || ""}\n[Payment: $${totalStr} via PayPal.me/${PAYPAL_ME_USERNAME} — pending owner reconciliation]`.trim();
      await submitRegistration({ notes });
      // Open PayPal.me in a new tab with the amount pre-filled
      const url = `https://paypal.me/${PAYPAL_ME_USERNAME}/${totalStr}`;
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success(`Registration saved! Complete your $${totalStr} payment in the PayPal tab that just opened.`, { duration: 9000 });
      setForm(empty);
      setSelected({});
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Couldn't submit — check required fields.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full border border-ink bg-white px-3 py-2 font-serif text-sm focus:outline-none focus:ring-2 focus:ring-ink";
  const labelCls = "font-mono text-[11px] uppercase tracking-[0.2em] text-ink/70";

  return (
    <form onSubmit={submitFree} data-testid="event-registration-form" className="border-2 border-ink bg-bone p-6 md:p-10">
      <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-ink/60">Registration</p>
      <h2 className="mt-2 font-alfa text-3xl uppercase leading-tight md:text-4xl">Small Town Showdown</h2>
      <p className="mt-3 font-serif text-sm text-ink/80">
        Load-in starts <b>Thursday at 1:00 PM</b>. Event kicks off <b>March 11th–14th, 2027</b> at
        {" "}<b>15362 Alric Pottberg Rd, Shady Hills, FL 34610</b>.
      </p>

      {/* Head Pit Master */}
      <fieldset className="mt-8">
        <legend className="font-alfa text-lg uppercase">Head Pit Master</legend>
        <div className="mt-3 grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className={labelCls}>First Name *</span>
            <input required data-testid="ev-first-name" className={inputCls + " mt-1"} value={form.first_name} onChange={upd("first_name")} />
          </label>
          <label className="block">
            <span className={labelCls}>Last Name *</span>
            <input required data-testid="ev-last-name" className={inputCls + " mt-1"} value={form.last_name} onChange={upd("last_name")} />
          </label>
        </div>
      </fieldset>

      {/* Team */}
      <fieldset className="mt-6">
        <legend className="font-alfa text-lg uppercase">Barbecue Team Name</legend>
        <input data-testid="ev-team-name" className={inputCls + " mt-3"} value={form.team_name} onChange={upd("team_name")} placeholder="Your BBQ team name" />
        <label className="mt-3 block">
          <span className={labelCls}>Size of Trailer &amp; Space Required</span>
          <input data-testid="ev-trailer-size" className={inputCls + " mt-1"} value={form.trailer_size} onChange={upd("trailer_size")} placeholder="e.g., 20 ft trailer, need 30×40 space" />
        </label>
      </fieldset>

      {/* Address */}
      <fieldset className="mt-6">
        <legend className="font-alfa text-lg uppercase">Address *</legend>
        <div className="mt-3 space-y-3">
          <label className="block">
            <span className={labelCls}>Street Address</span>
            <input required data-testid="ev-street" className={inputCls + " mt-1"} value={form.street} onChange={upd("street")} />
          </label>
          <label className="block">
            <span className={labelCls}>Street Address Line 2</span>
            <input data-testid="ev-street2" className={inputCls + " mt-1"} value={form.street2} onChange={upd("street2")} />
          </label>
          <div className="grid gap-3 md:grid-cols-3">
            <label className="block">
              <span className={labelCls}>City</span>
              <input required data-testid="ev-city" className={inputCls + " mt-1"} value={form.city} onChange={upd("city")} />
            </label>
            <label className="block">
              <span className={labelCls}>State / Province</span>
              <input required data-testid="ev-state" className={inputCls + " mt-1"} value={form.state} onChange={upd("state")} />
            </label>
            <label className="block">
              <span className={labelCls}>Postal / Zip</span>
              <input required data-testid="ev-zip" className={inputCls + " mt-1"} value={form.zip_code} onChange={upd("zip_code")} />
            </label>
          </div>
        </div>
      </fieldset>

      {/* Contact */}
      <fieldset className="mt-6 grid gap-4 md:grid-cols-2">
        <label className="block">
          <span className={labelCls}>Email *</span>
          <input required type="email" data-testid="ev-email" className={inputCls + " mt-1"} value={form.email} onChange={upd("email")} placeholder="you@example.com" />
        </label>
        <label className="block">
          <span className={labelCls}>Phone Number *</span>
          <input required type="tel" data-testid="ev-phone" className={inputCls + " mt-1"} value={form.phone} onChange={upd("phone")} placeholder="(000) 000-0000" />
        </label>
      </fieldset>

      {/* Contest entries */}
      <fieldset className="mt-8">
        <legend className="font-alfa text-lg uppercase">Contest Entries</legend>
        <div className="mt-3 grid gap-3">
          {CATEGORIES.map((c) => {
            const on = Boolean(selected[c.id]);
            return (
              <div key={c.id} className={`border p-4 transition-colors ${on ? "border-ink bg-white" : "border-ink/30 bg-bone/40"}`}>
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id={`cat-${c.id}`}
                    data-testid={`ev-cat-${c.id}`}
                    checked={on}
                    onChange={() => toggle(c.id)}
                    className="mt-1 h-5 w-5 accent-ink"
                  />
                  <div className="flex-1">
                    <label htmlFor={`cat-${c.id}`} className="block cursor-pointer">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="font-alfa text-base uppercase leading-tight">{c.name}</span>
                        <span className="font-mono text-sm font-bold">
                          {c.price === 0 ? "FREE" : `$${c.price.toFixed(2)}`}
                        </span>
                      </div>
                      <p className="mt-1 font-serif text-xs text-ink/70">{c.desc}</p>
                    </label>
                    {on && c.price > 0 && (
                      <div className="mt-3 flex items-center gap-2">
                        <span className={labelCls}>Qty</span>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          data-testid={`ev-qty-${c.id}`}
                          value={selected[c.id]}
                          onChange={(e) => setQty(c.id, e.target.value)}
                          className="w-16 border border-ink bg-white px-2 py-1 text-center font-mono text-sm"
                        />
                        <span className="font-mono text-xs text-ink/70">
                          = ${(c.price * selected[c.id]).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </fieldset>

      {/* Notes */}
      <label className="mt-6 block">
        <span className={labelCls}>Notes to the pit crew (optional)</span>
        <textarea rows={3} data-testid="ev-notes" className={inputCls + " mt-1"} value={form.notes} onChange={upd("notes")} placeholder="Special requests, arrival time, etc." />
      </label>

      {/* Total + submit / PayPal */}
      <div className="mt-8 border-t-2 border-ink pt-6">
        <div className="flex flex-col items-stretch justify-between gap-4 md:flex-row md:items-center">
          <div>
            <p className={labelCls}>Total Due Now</p>
            <p data-testid="ev-total" className="mt-1 font-alfa text-3xl">${totalStr}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
              {chosen.length} {chosen.length === 1 ? "entry" : "entries"} selected
            </p>
          </div>
        </div>

        {/* Payment/submit gate */}
        {!formValid ? (
          <div data-testid="ev-gate" className="mt-6 border-2 border-ink/20 bg-ink/5 px-4 py-4 text-center font-mono text-[11px] uppercase tracking-[0.24em] text-ink/60">
            Fill required fields & pick at least one entry to check out
          </div>
        ) : total === 0 ? (
          // All-free registration (only People's Choice picked) — just submit
          <button
            type="submit"
            disabled={busy}
            data-testid="ev-submit"
            className="mt-6 w-full border-2 border-ink bg-ink px-8 py-4 font-alfa text-lg uppercase tracking-[0.2em] text-bone transition-colors hover:bg-transparent hover:text-ink disabled:opacity-50"
          >
            {busy ? "Submitting…" : "Register My Team (Free)"}
          </button>
        ) : (
          // Paid — save registration then redirect to PayPal.me with amount pre-filled
          <div className="mt-6 space-y-3" data-testid="ev-paypal-wrap">
            <button
              type="button"
              onClick={submitPaid}
              disabled={busy}
              data-testid="ev-paypal-btn"
              className="flex w-full items-center justify-center gap-3 border-2 border-ink bg-[#0070ba] px-8 py-4 font-alfa text-lg uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#005a94] disabled:opacity-50"
            >
              {busy ? "Saving…" : (
                <>
                  <span>Pay ${totalStr} with PayPal</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M7.5 22h-3l3-19h6.5c3.5 0 5.5 1.8 5 5-.5 3.5-3 5.5-6.5 5.5H10l-2.5 8.5zm3-11h2.5c1.7 0 3-1 3.2-2.7.2-1.7-.8-2.3-2.5-2.3H11l-.5 5z"/>
                  </svg>
                </>
              )}
            </button>
            <p className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
              Opens paypal.me/{PAYPAL_ME_USERNAME} in a new tab with your ${totalStr} pre-filled.
              Your registration is saved immediately — the pit crew will confirm your payment when it lands.
            </p>
          </div>
        )}
      </div>
    </form>
  );
}
